import { Link, useParams } from 'react-router-dom'
import { ChevronLeft } from 'lucide-react'
import type { BookingPacketDto } from '@lib/dto/sales-booking.dto'
import { SALES_PACKET_STATUS_LABELS } from '@lib/enums/sales-packet.enum'
import { BookingInvoices } from '@/components/sales/BookingInvoices'
import { BookingPayments } from '@/components/sales/BookingPayments'
import { ProposalView } from '@/components/sales/ProposalView'
import { PageContainer } from '@/components/ui/page-container'
import { DetailSkeleton, ErrorState } from '@/components/ui/states'
import { requestErrorMessage } from '@/helpers/apiClient'
import { useBooking } from '@/hooks/useSales'
import { formatEventDate } from '@/lib/eventTime'

/**
 * A proposal the couple can still act on, or whose lapse they should hear
 * about. Invoices sit apart. A change that lapsed leaves the agreement as it
 * was, so the agreement stays in front.
 */
function current(packet: BookingPacketDto): boolean {
  if (packet.kind === 'invoice') return false
  if (packet.kind === 'amendment' && packet.status === 'expired') return false
  return packet.status === 'open' || packet.status === 'signing' || packet.status === 'signed' || packet.status === 'expired'
}

/** "Withdrawn Oct 21, 2026: a proposal for Jun 5, 2027" */
function closedLine(packet: BookingPacketDto): string {
  const when = packet.closedAt === null ? '' : ` ${new Date(packet.closedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`
  const what = packet.kind === 'amendment' ? 'a change to the agreement, for' : 'a proposal for'
  return `${SALES_PACKET_STATUS_LABELS[packet.status]}${when}: ${what} ${formatEventDate(packet.date)}`
}

/** The page's title: the agreement once signed, a change while one is out, else the proposal. */
function titleOf(latest: BookingPacketDto | undefined, venueName: string): string {
  if (latest?.status === 'signed') return `Your agreement with ${venueName}`
  if (latest?.kind === 'amendment') return `A change to your agreement with ${venueName}`
  return `Your proposal from ${venueName}`
}

/**
 * The couple's proposal page, `/events/:eventId/booking` (docs/sales.spec.md,
 * "Choosing and signing"), which their email links to. Their planner sees the
 * same, and can't choose.
 */
export function BookingPage() {
  const { eventId = '' } = useParams()
  const booking = useBooking(eventId)

  function body() {
    if (booking.isPending) return <DetailSkeleton />
    if (booking.isError) return <ErrorState message={requestErrorMessage(booking.error)} onRetry={() => void booking.refetch()} />
    const [latest, ...rest] = booking.data.packets.filter(current)
    const closed = booking.data.packets.filter((p) => !current(p) && p.kind !== 'invoice')
    const invoices = booking.data.packets.filter((p) => p.kind === 'invoice')
    return (
      <>
        <header className="space-y-1">
          <p className="m-0 text-sm text-ink-muted">{booking.data.eventName}</p>
          <h1 className="m-0 text-2xl font-semibold tracking-tight text-ink">{titleOf(latest, booking.data.venueName)}</h1>
        </header>
        {latest === undefined ? (
          <p className="m-0 text-sm text-ink-muted">There’s no proposal here right now. {booking.data.venueName} will email you when there is.</p>
        ) : (
          <ProposalView
            key={`${latest.id}-${String(booking.dataUpdatedAt)}`}
            eventId={eventId}
            venueName={booking.data.venueName}
            packet={latest}
            // A change still out shows the payments it would make; once signed, the real ones replace them.
            scheduled={latest.kind === 'amendment' && latest.status !== 'signed' ? false : booking.data.payments.some((p) => p.kind === 'installment')}
          />
        )}
        <BookingInvoices invoices={invoices} />
        <BookingPayments booking={booking.data} />
        {[...rest, ...closed].length === 0 ? null : (
          <section className="space-y-2 border-t border-line pt-6">
            <h2 className="m-0 text-sm font-semibold text-ink">Earlier</h2>
            <ul className="m-0 list-none space-y-1 p-0 text-sm text-ink-muted">
              {[...rest, ...closed].map((p) => (
                <li key={p.id}>{closedLine(p)}</li>
              ))}
            </ul>
          </section>
        )}
      </>
    )
  }

  return (
    <PageContainer className="space-y-6">
      <Link to={`/events/${eventId}`} className="inline-flex items-center gap-1 text-sm text-ink-muted transition-colors hover:text-ink">
        <ChevronLeft className="h-4 w-4" /> Your event
      </Link>
      {body()}
    </PageContainer>
  )
}
